import React, { useState } from 'react';
import './QuotationsList.css';

const QuotationsList = ({ 
  quotations, 
  loading, 
  error, 
  searchTerm, 
  setSearchTerm,
  filters,
  updateFilters,
  clearFilters,
  handleSort, 
  getSortIndicator,
  formatDate,
  formatCurrency,
  getQuotationStatusColor,
  calculateQuotationTotal,
  onViewQuotation
}) => {
  const [selectedClient, setSelectedClient] = useState('');

  // Get unique clients for filter dropdown
  const uniqueClients = [...new Set(quotations.map(q => q.clientId))].sort();

  const handleClientFilterChange = (clientId) => {
    setSelectedClient(clientId);
    updateFilters({ clientId: clientId || '' });
  };

  const handleStatusFilterChange = (status) => {
    updateFilters({ quoteStatus: status });
  };

  const getStatusDisplay = (status) => {
    const statusMap = {
      'open': 'Aberta',
      'expired': 'Expirada', 
      'accepted': 'Aceita',
      'rejected': 'Rejeitada'
    };
    return statusMap[status] || status;
  };

  if (loading) {
    return (
      <div className="quotations-loading">
        <div className="spinner"></div>
        <p>Carregando cotações...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="quotations-error">
        <p>⚠️ {error}</p>
      </div>
    );
  }

  return (
    <div className="quotations-list">
      {/* Filters Section */}
      <div className="quotations-filters">
        <div className="filter-group">
          <label htmlFor="search">Buscar:</label>
          <input
            id="search"
            type="text"
            placeholder="Buscar por ID, cliente, status..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="filter-input"
          />
        </div>

        <div className="filter-group">
          <label htmlFor="client-filter">Cliente:</label>
          <select
            id="client-filter"
            value={selectedClient}
            onChange={(e) => handleClientFilterChange(e.target.value)}
            className="filter-select"
          >
            <option value="">Todos os clientes</option>
            {uniqueClients.map(clientId => (
              <option key={clientId} value={clientId}>{clientId}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="status-filter">Status:</label>
          <select
            id="status-filter"
            value={filters.quoteStatus}
            onChange={(e) => handleStatusFilterChange(e.target.value)}
            className="filter-select"
          >
            <option value="">Todos os status</option>
            <option value="open">Aberta</option>
            <option value="expired">Expirada</option>
            <option value="accepted">Aceita</option>
            <option value="rejected">Rejeitada</option>
          </select>
        </div>

        <button 
          onClick={clearFilters}
          className="clear-filters-btn"
          disabled={!filters.clientId && !filters.quoteStatus}
        >
          Limpar Filtros
        </button>
      </div>

      {/* Quotations Table */}
      <div className="quotations-table-wrapper">
        <table className="quotations-table">
          <thead>
            <tr>
              <th 
                onClick={() => handleSort('quotationId')} 
                className="sortable"
              >
                ID {getSortIndicator('quotationId')}
              </th>
              <th 
                onClick={() => handleSort('clientId')} 
                className="sortable"
              >
                Cliente {getSortIndicator('clientId')}
              </th>
              <th 
                onClick={() => handleSort('cliente.name')} 
                className="sortable"
              >
                Nome do Cliente {getSortIndicator('cliente.name')}
              </th>
              <th 
                onClick={() => handleSort('quoteStatus')} 
                className="sortable"
              >
                Status {getSortIndicator('quoteStatus')}
              </th>
              <th 
                onClick={() => handleSort('timeCreated')} 
                className="sortable"
              >
                Data de Criação {getSortIndicator('timeCreated')}
              </th>
              <th className="amount-column">
                Valor Total
              </th>
              <th className="actions-column">
                Ações
              </th>
            </tr>
          </thead>
          <tbody>
            {quotations.length === 0 ? (
              <tr>
                <td colSpan="7" className="no-data">
                  Nenhuma cotação encontrada
                </td>
              </tr>
            ) : (
              quotations.map((quotation) => (
                <tr key={quotation.quotationId} className="quotation-row">
                  <td className="quotation-id">
                    {quotation.quotationId}
                  </td>
                  <td className="client-id">
                    {quotation.clientId}
                  </td>
                  <td className="client-name">
                    {quotation.cliente?.name || 'N/A'}
                  </td>
                  <td className="status-cell">
                    <span 
                      className="status-badge"
                      style={{ backgroundColor: getQuotationStatusColor(quotation.quoteStatus) }}
                    >
                      {getStatusDisplay(quotation.quoteStatus)}
                    </span>
                  </td>
                  <td className="date-cell">
                    {formatDate(quotation.timeCreated)}
                  </td>
                  <td className="amount-cell">
                    {formatCurrency(calculateQuotationTotal(quotation))}
                  </td>
                  <td className="actions-cell">
                    <button
                      onClick={() => onViewQuotation(quotation)}
                      className="view-btn"
                      title="Visualizar Cotação"
                    >
                      👁️ Ver
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Summary */}
      {quotations.length > 0 && (
        <div className="quotations-summary">
          <p>Total de cotações: <strong>{quotations.length}</strong></p>
        </div>
      )}
    </div>
  );
};

export default QuotationsList;
